// «Возвращение» — экран после перерыва от четырёх дней, вместо обычного
// «Сегодня». Макет — design/redesign.mockup.html; решение владельца от
// 28 октября 2026 («если долго не было, подбодрить»), реплика — тренер
// (src/lib/coach-lines.ts) вместо слов ученика со старта: docs/02-decisions.md,
// блок «2026-10-30 · Тренер вместо слов ученика».
//
// Что здесь говорится — только факты и один шаг: сколько держится, что пора
// повторить, где сейчас план. Упрёка нет, похвалы нет. Главная кнопка — короткий
// набор повторения: первый день после перерыва должен быть лёгким для входа.

import type { DayPlan, ProgressSnapshot } from "../api";
import { plural } from "../lib/dates";
import { CoachNote } from "../ui/CoachNote";

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const dayMonth = (date: string) => {
  const [, m, d] = date.split("-").map(Number);
  return `${d} ${MONTHS[m! - 1]}`;
};

export function Return({
  learnerKey,
  plan,
  snapshot,
  gap,
  starting,
  onShort,
  onSkip,
}: {
  learnerKey: string;
  plan: DayPlan;
  snapshot: ProgressSnapshot;
  /** Дней перерыва — src/lib/coach-state.ts, `breakDays`. */
  gap: number;
  starting: boolean;
  /** Короткий набор повторения — и сразу в первую задачу. */
  onShort: () => void;
  /** К обычному «Сегодня». */
  onSkip: () => void;
}) {
  const open = snapshot.openSkills;
  const due = open.filter((s) => s.due);
  const held = open.length - due.length;
  const last = snapshot.days.find((d) => d.date < plan.date && d.tasks > 0);
  const topic = snapshot.planWeek[0] ?? null;
  const dueNames = due.slice(0, 2).map((s) => s.name);

  return (
    <div className="ret">
      <div className="ret-head">
        <span className="stamp" aria-hidden="true">
          {gap} {plural(gap, "день", "дня", "дней")}
        </span>
        <div className="eyebrow">
          Перерыв {gap} {plural(gap, "день", "дня", "дней")}
          {last ? ` · последний раз ${dayMonth(last.date)}` : ""}
        </div>
        <h1 className="h1">{plan.name ? `С возвращением, ${plan.name}` : "С возвращением"}</h1>
        <p className="lede">
          {due.length === 0
            ? "Всё открытое держится. Можно сразу продолжать по плану."
            : "Начнём с того, что начало забываться: короткий набор, около восьми минут."}
        </p>
      </div>

      <div className="ret-facts">
        <div className="ret-fact">
          <b className="num">
            {held} / {open.length}
          </b>
          <span>
            {plural(open.length, "навык", "навыка", "навыков")} держится — повторять пока не нужно
          </span>
        </div>
        <div className="ret-fact">
          <b className="num">{due.length}</b>
          <span>
            {due.length === 0
              ? "пора повторить — ничего"
              : `пора повторить: ${dueNames.join(", ")}${due.length > dueNames.length ? ` и ещё ${due.length - dueNames.length}` : ""}`}
          </span>
        </div>
        <div className="ret-fact">
          <b className="num">{plan.daysToExam}</b>
          <span>
            {plural(plan.daysToExam, "день", "дня", "дней")} до экзамена
            {topic ? ` · тема недели «${topic.title}»` : ""}
          </span>
        </div>
      </div>

      <CoachNote className="ret-coach" situation="return" vars={{ days: gap }} seedParts={[learnerKey, plan.date]} />

      <div className="row row--actions ret-act">
        {due.length > 0 ? (
          <button type="button" className="btn btn--primary btn--big" disabled={starting} onClick={onShort}>
            Вернуться — повторение, ≈ 8 мин
          </button>
        ) : (
          <button type="button" className="btn btn--primary btn--big" onClick={onSkip}>
            Продолжить по плану
          </button>
        )}
        {due.length > 0 ? (
          <button type="button" className="link today-hero__alt" onClick={onSkip}>
            Обычный набор дня
          </button>
        ) : null}
      </div>
    </div>
  );
}
